/**
 * Компонент списка объектов сотрудника
 */
import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { WorkerApi, type SiteInfo } from '../services/apiClient'
import { useWorkerStore } from '../store/workerStore'
import './SitesList.css'

export function SitesList() {
  const navigate = useNavigate()
  const { employeeId } = useWorkerStore()
  const [sites, setSites] = useState<SiteInfo[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null)

  const loadSites = useCallback(async () => {
    if (!employeeId) return
    
    setIsLoading(true)
    setError(null)
    
    try {
      const response = await WorkerApi.getEmployeeSites(employeeId)
      setSites(response.sites)
      setLastUpdate(new Date())
    } catch (err) {
      console.error('Failed to load sites:', err)
      
      // Проверяем, является ли это ошибкой удаленного сотрудника
      const { EmployeeDeletedError } = await import('../errors/EmployeeDeletedError')
      if (err instanceof EmployeeDeletedError) {
        navigate('/employee-deleted')
        return
      }
      
      setError(err instanceof Error ? err.message : 'Ошибка загрузки')
    } finally {
      setIsLoading(false)
    }
  }, [employeeId, navigate])

  // Загружаем при монтировании
  useEffect(() => {
    loadSites()
  }, [loadSites])

  // Автообновление каждые 30 секунд
  useEffect(() => {
    const interval = setInterval(loadSites, 30000)
    return () => clearInterval(interval)
  }, [loadSites])

  return (
    <div className="sites-container">
      <div className="sites-content">
          {isLoading && sites.length === 0 ? (
            <div className="sites-loading">
              <div className="sites-spinner"></div>
              <p>Загрузка...</p>
            </div>
          ) : error ? (
            <div className="sites-error">
              <span className="sites-error-icon">⚠️</span>
              <p>{error}</p>
              <button className="sites-retry" onClick={loadSites}>
                Повторить
              </button>
            </div>
          ) : sites.length === 0 ? (
            <div className="sites-empty">
              <span className="sites-empty-icon">📭</span>
              <p>Нет доступных объектов</p>
              <p className="sites-empty-hint">Обратитесь к администратору</p>
            </div>
          ) : (
            <>
              <ul className="sites-list">
                {sites.map(site => (
                  <li key={site.id} className="sites-item">
                    <div className="sites-item-icon">🏗️</div>
                    <div className="sites-item-info">
                      <div className="sites-item-name">{site.name}</div>
                      {site.address && (
                        <div className="sites-item-address">{site.address}</div>
                      )}
                    </div>
                    <div className={`sites-item-status ${site.is_active ? 'active' : 'inactive'}`}>
                      {site.is_active ? '✓' : '×'}
                    </div>
                  </li>
                ))}
              </ul>
              
              {lastUpdate && (
                <div className="sites-footer">
                  <span className="sites-update-time">
                    Обновлено: {lastUpdate.toLocaleTimeString('ru-RU')}
                  </span>
                  <button 
                    className="sites-refresh" 
                    onClick={loadSites}
                    disabled={isLoading}
                  >
                    {isLoading ? '⏳' : '🔄'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
    </div>
  )
}

